import { Controller } from "@hotwired/stimulus"
import { track as trackEvent } from "../lib/mixpanel"

// Phase 2.10 Tier 2 — Finalize / Unlock kebab actions.
//
// Mounted by the `shared/_finalize_kebab_menu.html.erb` partial on
// every Final-badge-bearing row / show page. Handles the
// "Mark as Final" + "Unlock" + "View Audit Trail" + "Replace
// version" affordances. Confirmation modals follow CLAUDE.md "no
// browser confirm()" — proper styled dialogs with backdrop dismiss.
export default class extends Controller {
  static targets = ["markFinalModal", "markFinalReason", "unlockModal", "unlockReason",
                    "watchButton", "watchLabel"]

  static values = {
    targetType: String,
    targetId: Number,
    // Per-sequence disambiguation for legacy-finance surrogates
    // (PR1 vs PR2 on the same project). Modern targets pass `0`.
    targetSequence: Number,
    targetOvid: Number,
    markFinalUrl: String,
    unlockUrl: String,
    // Phase 2.10 Tier 4
    watchUrl: String,
    watched: Boolean,
    auditEndpoint: String,  // Phase 2.10 Tier 4 — legacy-finance audit drawer fetch URL
  }

  // ── Audit drawer ────────────────────────────────────────────────

  async openAuditDrawer(event) {
    if (event) event.preventDefault()

    // Phase 2.10 Tier 4 — when an audit_endpoint is configured (legacy-
    // finance kebab), fetch the drawer fragment first, inject into
    // a slot below the kebab, then dispatch open. Modern surfaces
    // already have the drawer pre-rendered; they just dispatch.
    if (this.hasAuditEndpointValue && this.auditEndpointValue.length > 0) {
      try {
        const resp = await fetch(this.auditEndpointValue, {
          method: "GET",
          headers: { "Accept": "text/html" },
        })
        if (!resp.ok) {
          this._toast("Could not load audit trail.", "alert")
          return
        }
        const html = await resp.text()
        const slot = this._auditSlot()
        slot.innerHTML = html
        // Stimulus auto-binds new controllers via MutationObserver
        // (microtask). Wait one frame so the freshly-injected drawer's
        // `connect()` registers its window listener before we dispatch
        // — otherwise the open event fires into the void and the
        // drawer never reveals.
        await new Promise(requestAnimationFrame)
      } catch (e) {
        this._toast(`Network error: ${e.message}`, "alert")
        return
      }
    }

    window.dispatchEvent(
      new CustomEvent("document-audit-drawer:open", {
        detail: { kind: this.targetTypeValue, id: this.targetIdValue },
      }),
    )
  }

  // Lazily-created sibling slot used by the legacy-finance audit
  // drawer fetch path. Append to body so the drawer's `fixed`
  // positioning isn't constrained by an ancestor `overflow: hidden`.
  _auditSlot() {
    let slot = document.querySelector("#legacy-finance-audit-drawer-slot")
    if (!slot) {
      slot = document.createElement("div")
      slot.id = "legacy-finance-audit-drawer-slot"
      document.body.appendChild(slot)
    }
    return slot
  }

  // ── Mark as Final ───────────────────────────────────────────────

  openMarkFinalModal(event) {
    if (event) event.preventDefault()
    if (this.hasMarkFinalReasonTarget) this.markFinalReasonTarget.value = ""
    this.markFinalModalTarget.classList.remove("hidden")
  }

  closeMarkFinalModal(event) {
    if (event) event.preventDefault()
    this.markFinalModalTarget.classList.add("hidden")
  }

  async submitMarkFinal(event) {
    if (event) event.preventDefault()
    const reason = this.hasMarkFinalReasonTarget ? this.markFinalReasonTarget.value.trim() : ""
    const body = new FormData()
    body.append("target_type", this.targetTypeValue)
    body.append("target_id", String(this.targetIdValue))
    if (reason.length > 0) body.append("reason", reason)

    try {
      const resp = await fetch(this.markFinalUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": this._csrfToken(), "Accept": "application/json" },
        body,
      })
      const json = await resp.json().catch(() => ({}))
      if (!resp.ok || json.ok === false) {
        const message = json.message || "Could not mark as Final."
        this._toast(message, "alert")
        return
      }

      trackEvent("Document Finalized", {
        target_type: this.targetTypeValue,
        target_id: this.targetIdValue,
        reason_present: reason.length > 0,
      })

      this.closeMarkFinalModal()
      this._flashAndReload("Document marked as Final.")
    } catch (e) {
      this._toast(`Network error: ${e.message}`, "alert")
    }
  }

  // ── Unlock ──────────────────────────────────────────────────────

  openUnlockModal(event) {
    if (event) event.preventDefault()
    if (this.hasUnlockReasonTarget) this.unlockReasonTarget.value = ""
    this.unlockModalTarget.classList.remove("hidden")
  }

  closeUnlockModal(event) {
    if (event) event.preventDefault()
    this.unlockModalTarget.classList.add("hidden")
  }

  async submitUnlock(event) {
    if (event) event.preventDefault()
    const reason = this.hasUnlockReasonTarget ? this.unlockReasonTarget.value.trim() : ""
    if (reason.length === 0) {
      this._toast("A reason is required to unlock a finalized document.", "alert")
      return
    }

    const body = new FormData()
    body.append("target_type", this.targetTypeValue)
    body.append("target_id", String(this.targetIdValue))
    body.append("reason", reason)

    try {
      const resp = await fetch(this.unlockUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": this._csrfToken(), "Accept": "application/json" },
        body,
      })
      const json = await resp.json().catch(() => ({}))
      if (!resp.ok || json.ok === false) {
        const message = json.message || "Could not unlock the document."
        this._toast(message, "alert")
        return
      }

      trackEvent("Document Unlocked", {
        target_type: this.targetTypeValue,
        target_id: this.targetIdValue,
      })

      this.closeUnlockModal()
      this._flashAndReload("Document unlocked.")
    } catch (e) {
      this._toast(`Network error: ${e.message}`, "alert")
    }
  }

  // ── Watch / Unwatch (Phase 2.10 Tier 4) ─────────────────────────

  async toggleWatch(event) {
    if (event) event.preventDefault()
    const wantWatched = !this.watchedValue
    const url = this.watchUrlValue
    const method = wantWatched ? "POST" : "DELETE"
    const body = new FormData()
    body.append("target_type", this.targetTypeValue)
    body.append("target_id", String(this.targetIdValue))
    if (this.hasTargetSequenceValue && this.targetSequenceValue > 0) {
      body.append("sequence", String(this.targetSequenceValue))
    }
    if (this.hasTargetOvidValue && this.targetOvidValue > 0) {
      body.append("ovid", String(this.targetOvidValue))
    }

    try {
      const resp = await fetch(url, {
        method,
        headers: { "X-CSRF-Token": this._csrfToken(), "Accept": "application/json" },
        body,
      })
      const json = await resp.json().catch(() => ({}))
      if (!resp.ok || json.ok === false) {
        const message = json.error === "watch_disabled"
          ? "Watch is disabled for your company."
          : (json.message || "Could not update watch.")
        this._toast(message, "alert")
        return
      }

      this.watchedValue = !!json.watched
      this._renderWatchState()

      trackEvent(wantWatched ? "Document Watched" : "Document Unwatched", {
        target_type: this.targetTypeValue,
        target_id: this.targetIdValue,
        sequence: this.hasTargetSequenceValue ? this.targetSequenceValue : 0,
        ovid: this.hasTargetOvidValue ? this.targetOvidValue : 0,
      })
      this._toast(wantWatched ? "Watching this document." : "Unwatched.", "notice")
    } catch (e) {
      this._toast(`Network error: ${e.message}`, "alert")
    }
  }

  _renderWatchState() {
    if (this.hasWatchLabelTarget) {
      this.watchLabelTarget.textContent = this.watchedValue ? "Unwatch" : "Watch this document"
    }
    // Star icon: when watched, swap to filled amber; when not, outline gray.
    if (this.hasWatchButtonTarget) {
      const svg = this.watchButtonTarget.querySelector("svg")
      if (svg) {
        if (this.watchedValue) {
          svg.classList.remove("text-gray-500")
          svg.classList.add("text-amber-500")
          svg.setAttribute("fill", "currentColor")
        } else {
          svg.classList.remove("text-amber-500")
          svg.classList.add("text-gray-500")
          svg.setAttribute("fill", "none")
        }
      }
    }
  }

  // ── Replace version (T2 stub — wired up per-resource in T3) ────

  openReplaceModal(event) {
    if (event) event.preventDefault()
    // Per-resource controllers (development_controller / drawings_specs /
    // specifications) already implement direct-S3 upload via
    // `register_upload`. T3 wires those flows under a unified Replace
    // modal. T2 dispatches an event; if no resource handles it the
    // toast fires.
    const handled = window.dispatchEvent(
      new CustomEvent("finalize-controls:open-replace-modal", {
        detail: { kind: this.targetTypeValue, id: this.targetIdValue },
        cancelable: true,
      }),
    )
    if (handled) return
    this._toast("Replace flow not wired on this surface yet.", "alert")
  }

  // ── Plumbing ────────────────────────────────────────────────────

  stopPropagation(event) {
    event.stopPropagation()
  }

  _csrfToken() {
    const el = document.querySelector('meta[name="csrf-token"]')
    return el ? el.content : ""
  }

  _flashAndReload(message) {
    try {
      sessionStorage.setItem("flash:notice", message)
    } catch (e) { /* sessionStorage may be disabled */ }
    window.location.reload()
  }

  _toast(message, type = "notice") {
    const el = document.createElement("div")
    el.className =
      type === "alert"
        ? "flash-toast-alert"
        : "flash-toast-notice"
    el.textContent = message
    el.dataset.controller = "flash"
    el.dataset.flashDismissAfterValue = "5000"
    document.body.prepend(el)
  }
}
